/**
 * Print Stars chapter — focus-palace star proof with definitions and
 * section explainers (extra sheet; not in the Canva 15-page set).
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  CircleDashed,
  Sparkles,
  Star,
  Wand2,
  Zap,
} from "lucide-react";
import type {
  FocusStarSourceMode,
  MonthlyConsultationBundle,
  StemSiHuaActivation,
} from "../../../../utils/forecast/monthlyConsultation";
import {
  activationLandingEnglish,
  SI_HUA_LABEL,
  SI_HUA_PLAIN,
  starToEnglish,
} from "../../../../utils/forecast/monthlyConsultation/englishLabels";
import heroStars from "../../../../assets/monthly-consultation/pdf/hero-stars.png";
import {
  kindFromActivationTitle,
  SiHuaGlyph,
  SI_HUA_COLOR,
} from "../../shared/SiHuaChip";
import {
  StarFamilyGlyph,
  starFamilyColor,
  starFamilyGlyph,
} from "../../shared/StarFamilyGlyph";
import {
  PdfCard,
  PdfChapterShell,
  PdfInsightStrip,
  PdfPill,
} from "../atoms";
import {
  STARS_CUES_BLURB,
  STARS_PAGE_INTRO,
  STARS_SIGNALS_BLURB,
  STARS_TRANSFORMS_BLURB,
  starsModeExplainer,
} from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  charcoal,
  cream,
  displayFontFamily,
  green,
  mutedGray,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Resolve webpack URL for the Stars celestial hero band.
 *
 * @returns Image URL, or empty when the module did not yield a path
 */
function starsHeroSrc(): string {
  return typeof heroStars === "string" ? heroStars.trim() : "";
}

/**
 * Props for the PDF stars chapter.
 */
export interface PdfChapterStarsProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/**
 * Terracotta subsection label with optional Lucide icon.
 *
 * @param props - Label text and optional icon component
 * @returns Section eyebrow row
 */
const PdfSectionLabel: React.FC<{
  readonly label: string;
  readonly Icon?: LucideIcon;
}> = ({ label, Icon }) => {
  const safe = typeof label === "string" ? label.trim() : "";
  if (safe.length === 0) {
    return null;
  }
  return (
    <p
      className="mc-pdf-section-rule"
      style={{
        margin: "0 0 6px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: terracotta,
        fontFamily: bodyFontFamily,
      }}
    >
      {Icon !== undefined ? (
        <Icon size={14} color={terracotta} strokeWidth={2.2} aria-hidden="true" />
      ) : null}
      {safe}
    </p>
  );
};

/**
 * Soft tint background from a theme hex (12% alpha via hex suffix).
 *
 * @param color - Theme hex color
 * @returns Light background tint
 */
function tokenTint(color: string): string {
  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    return `${color}1f`;
  }
  return `${mutedGray}1f`;
}

/**
 * Title-case a star-family key for the cue meta line.
 *
 * @param family - Family key from {@link starFamilyGlyph}
 * @returns Display label
 */
function titleCaseFamily(family: string): string {
  const trimmed = family.trim();
  if (trimmed.length === 0) {
    return "";
  }
  return `${trimmed.slice(0, 1).toUpperCase()}${trimmed.slice(1)}`;
}

/**
 * Meta line under a star name (family + palace / borrow source).
 *
 * @param family - Visual family key
 * @param isBorrowed - Whether the star is borrowed from the opposite palace
 * @param focusEnglish - Focus palace English name
 * @param oppositeEnglish - Opposite palace when borrowed
 * @returns Joined meta string
 */
function starLocationLine(
  family: string,
  isBorrowed: boolean,
  focusEnglish: string,
  oppositeEnglish: string | null
): string {
  const familyTitle = titleCaseFamily(family);
  if (isBorrowed && oppositeEnglish !== null) {
    return [familyTitle, `from ${oppositeEnglish}`].join(" · ");
  }
  if (focusEnglish.length > 0) {
    return [familyTitle, `in ${focusEnglish}`].join(" · ");
  }
  return familyTitle;
}

/**
 * Human-readable focus mode label.
 *
 * @param mode - Focus star source mode
 * @returns Display label
 */
function modeLabel(mode: FocusStarSourceMode): string {
  if (mode === "natal") {
    return "Natal stars in focus palace";
  }
  if (mode === "borrowed") {
    return "Borrowed from opposite palace";
  }
  return "Open palace, no main stars";
}

/**
 * Whether an activation lands on the focus palace (chart or natal English).
 *
 * @param activation - Stem activation
 * @param focusEnglish - Focus palace English name
 * @param natalEnglish - Natal English name for the physical palace
 * @returns True when landing matches focus
 */
function landsOnFocus(
  activation: StemSiHuaActivation,
  focusEnglish: string,
  natalEnglish: string
): boolean {
  const landing = activationLandingEnglish(activation).trim().toLowerCase();
  const focus = focusEnglish.trim().toLowerCase();
  const natal = natalEnglish.trim().toLowerCase();
  return (
    (focus.length > 0 && landing === focus) ||
    (natal.length > 0 && landing === natal) ||
    (focus.length > 0 && landing.includes(focus.replace(/ palace$/i, "")))
  );
}

/**
 * Focus palace stars with definitions, family glyphs, cues, transforms, and signals.
 */
export const PdfChapterStars: React.FC<PdfChapterStarsProps> = ({
  bundle,
  pageNumber,
}) => {
  const source = bundle.focusStarSource;
  const spotlight = Array.isArray(bundle.starSpotlight)
    ? bundle.starSpotlight
    : [];
  const life = bundle.lifePalaceStars;
  const transforms = Array.isArray(life.natalTransforms)
    ? life.natalTransforms
    : [];
  const activations = Array.isArray(bundle.stemActivations)
    ? bundle.stemActivations
    : [];
  const activationCards = Array.isArray(bundle.activationCards)
    ? bundle.activationCards
    : [];

  const focusEnglish =
    typeof source.focusPalaceNameEnglish === "string"
      ? source.focusPalaceNameEnglish.trim()
      : "";
  const oppositeEnglish =
    typeof source.borrowedFromPalaceNameEnglish === "string"
      ? source.borrowedFromPalaceNameEnglish.trim()
      : null;
  const natalEnglish =
    typeof life.palaceNameEnglish === "string"
      ? life.palaceNameEnglish.trim()
      : "";

  const relatedCards = activationCards.filter((card) => {
    const landing =
      typeof card.landing === "string" ? card.landing.trim().toLowerCase() : "";
    const focus = focusEnglish.toLowerCase();
    const natal = natalEnglish.toLowerCase();
    return (
      (focus.length > 0 && landing === focus) ||
      (natal.length > 0 && landing === natal) ||
      activations.some(
        (row) =>
          landsOnFocus(row, focusEnglish, natalEnglish) &&
          card.title.includes(starToEnglish(row.starName))
      )
    );
  });

  const starRows = spotlight.filter(
    (row) => row.kind === "star" || row.kind === "borrowed-star"
  );
  const teachingRows = spotlight.filter((row) => row.kind === "empty-meaning");
  const mode: FocusStarSourceMode = source.mode;
  const modeBody = starsModeExplainer(mode, focusEnglish, oppositeEnglish);
  const hero = starsHeroSrc();

  /** Three visual definition chips for natal / borrowed / open. */
  const meaningChips: ReadonlyArray<{
    readonly Icon: LucideIcon;
    readonly title: string;
    readonly body: string;
    readonly color: string;
  }> = [
    {
      Icon: Star,
      title: "Natal",
      body: "Stars that live in the palace permanently.",
      color: tanGold,
    },
    {
      Icon: ArrowLeftRight,
      title: "Borrowed",
      body: "From the opposite palace when focus has no main stars.",
      color: terracotta,
    },
    {
      Icon: CircleDashed,
      title: "Open",
      body: "No main stars either side — month activations lead.",
      color: mutedGray,
    },
  ];

  return (
    <PdfChapterShell
      chapterId="stars"
      eyebrow="+ STARS"
      title="Stars in the activated palace"
      breakBefore="always"
      compact
      fillPage={true}
      pageNumber={pageNumber}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          flex: "1 1 auto",
          minHeight: 0,
          fontFamily: bodyFontFamily,
        }}
      >
        {hero.length > 0 ? (
          <img
            src={hero}
            alt=""
            className="mc-pdf-hero-img mc-pdf-hero-img--stars"
            style={{ flexShrink: 0 }}
          />
        ) : null}

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: tokenTint(terracotta),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Sparkles size={18} color={terracotta} strokeWidth={2.2} />
          </span>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              lineHeight: 1.5,
              color: mutedGray,
              minWidth: 0,
              flex: 1,
            }}
          >
            {STARS_PAGE_INTRO}
          </p>
        </div>

        <PdfCard>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              alignItems: "center",
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            <p
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: terracotta,
              }}
            >
              <Star size={12} color={terracotta} strokeWidth={2.4} aria-hidden="true" />
              {modeLabel(mode)}
            </p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {focusEnglish.length > 0 ? (
                <PdfPill
                  label={focusEnglish}
                  color={tanGold}
                  bg={tokenTint(tanGold)}
                />
              ) : null}
              {mode === "borrowed" && oppositeEnglish !== null ? (
                <PdfPill
                  label={`From ${oppositeEnglish}`}
                  color={terracotta}
                  bg={tokenTint(terracotta)}
                />
              ) : null}
            </div>
          </div>
          <p
            style={{
              margin: "0 0 10px",
              fontSize: 12,
              lineHeight: 1.5,
              color: charcoal,
            }}
          >
            {modeBody}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
              paddingTop: 10,
              borderTop: `1px solid ${border}`,
            }}
          >
            {meaningChips.map((chip) => {
              const ChipIcon = chip.Icon;
              return (
                <div
                  key={chip.title}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    background: cream,
                    border: `1px solid ${border}`,
                    borderRadius: 12,
                    padding: "8px 9px",
                    boxSizing: "border-box",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: chip.color,
                    }}
                  >
                    <ChipIcon
                      size={12}
                      color={chip.color}
                      strokeWidth={2.3}
                      aria-hidden="true"
                    />
                    {chip.title}
                  </span>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 10,
                      lineHeight: 1.4,
                      color: mutedGray,
                    }}
                  >
                    {chip.body}
                  </p>
                </div>
              );
            })}
          </div>
        </PdfCard>

        {starRows.length > 0 ? (
          <div style={{ flexShrink: 0 }}>
            <PdfSectionLabel label="Star cues" Icon={Star} />
            <p
              style={{
                margin: "0 0 8px",
                fontSize: 11,
                lineHeight: 1.45,
                color: mutedGray,
              }}
            >
              {STARS_CUES_BLURB}
            </p>
            <PdfCard>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {starRows.map((row) => {
                  const label =
                    typeof row.star === "string" && row.star.trim().length > 0
                      ? starToEnglish(row.star)
                      : "Key star";
                  const color = starFamilyColor(label);
                  const family = starFamilyGlyph(label);
                  const cue = typeof row.cue === "string" ? row.cue.trim() : "";
                  const isBorrowed = row.kind === "borrowed-star";
                  const meta = starLocationLine(
                    family,
                    isBorrowed,
                    focusEnglish,
                    oppositeEnglish
                  );
                  return (
                    <div
                      key={`${row.kind}-${label}-${cue.slice(0, 16)}`}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "flex-start",
                        background: cream,
                        border: `1px solid ${border}`,
                        borderLeft: `4px solid ${color}`,
                        borderRadius: 12,
                        padding: "8px 10px",
                      }}
                    >
                      <span
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 10,
                          background: tokenTint(color),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                        aria-hidden="true"
                      >
                        <StarFamilyGlyph starName={label} size={18} />
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontFamily: displayFontFamily,
                              fontSize: 14,
                              fontWeight: 700,
                              color: charcoal,
                              lineHeight: 1.25,
                            }}
                          >
                            {label}
                          </p>
                          {isBorrowed ? (
                            <PdfPill
                              label="Borrowed"
                              color={tanGold}
                              bg={tokenTint(tanGold)}
                            />
                          ) : null}
                        </div>
                        {meta.length > 0 ? (
                          <p
                            style={{
                              margin: "2px 0 0",
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "0.10em",
                              textTransform: "uppercase",
                              color,
                            }}
                          >
                            {meta}
                          </p>
                        ) : null}
                        {cue.length > 0 ? (
                          <p
                            style={{
                              margin: "4px 0 0",
                              fontSize: 12,
                              lineHeight: 1.4,
                              color: mutedGray,
                            }}
                          >
                            {cue}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </PdfCard>
          </div>
        ) : (
          <PdfCard>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                lineHeight: 1.45,
                color: mutedGray,
              }}
            >
              {teachingRows[0]?.cue ??
                "No main stars sit in this focus palace this month. Read the signals below for how to work the open space."}
            </p>
          </PdfCard>
        )}

        {transforms.length > 0 ? (
          <div style={{ flexShrink: 0 }}>
            <PdfSectionLabel label="Natal transforms here" Icon={Wand2} />
            <p
              style={{
                margin: "0 0 8px",
                fontSize: 11,
                lineHeight: 1.45,
                color: mutedGray,
              }}
            >
              {STARS_TRANSFORMS_BLURB}
            </p>
            <PdfCard>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                }}
              >
                {transforms.map((row) => {
                  const kind = row.kind;
                  const color = SI_HUA_COLOR[kind];
                  const starEn = starToEnglish(row.starName);
                  const plain = SI_HUA_PLAIN[kind];
                  return (
                    <div
                      key={`${row.starName}-${kind}`}
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-start",
                        background: cream,
                        border: `1px solid ${border}`,
                        borderRadius: 12,
                        padding: "8px 10px",
                      }}
                    >
                      <span
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          background: tokenTint(color),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: 1,
                        }}
                        aria-hidden="true"
                      >
                        <SiHuaGlyph kind={kind} size={16} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 12,
                            fontWeight: 700,
                            color: charcoal,
                          }}
                        >
                          {starEn}
                        </p>
                        <p
                          style={{
                            margin: "2px 0 0",
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color,
                          }}
                        >
                          {SI_HUA_LABEL[kind]}
                        </p>
                        <p
                          style={{
                            margin: "4px 0 0",
                            fontSize: 10,
                            lineHeight: 1.4,
                            color: mutedGray,
                          }}
                        >
                          {plain}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </PdfCard>
          </div>
        ) : null}

        {relatedCards.length > 0 ? (
          <div style={{ flexShrink: 0 }}>
            <PdfSectionLabel label="Month signals in this palace" Icon={Zap} />
            <p
              style={{
                margin: "0 0 8px",
                fontSize: 11,
                lineHeight: 1.45,
                color: mutedGray,
              }}
            >
              {STARS_SIGNALS_BLURB}
            </p>
            <PdfCard>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {relatedCards.slice(0, 3).map((card) => {
                  const kind = kindFromActivationTitle(card.title);
                  const color = kind !== null ? SI_HUA_COLOR[kind] : green;
                  return (
                    <div
                      key={`rel-${String(card.urgencyRank)}-${card.title}`}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "flex-start",
                        background: cream,
                        border: `1px solid ${border}`,
                        borderLeft: `4px solid ${color}`,
                        borderRadius: 12,
                        padding: "8px 10px",
                      }}
                    >
                      {kind !== null ? (
                        <span
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            background: tokenTint(color),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                          aria-hidden="true"
                        >
                          <SiHuaGlyph kind={kind} size={16} />
                        </span>
                      ) : null}
                      <div style={{ minWidth: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 12,
                            fontWeight: 700,
                            color: charcoal,
                          }}
                        >
                          {card.title}
                        </p>
                        {typeof card.meaning === "string" &&
                        card.meaning.trim().length > 0 ? (
                          <p
                            style={{
                              margin: "3px 0 0",
                              fontSize: 11,
                              lineHeight: 1.4,
                              color: mutedGray,
                            }}
                          >
                            {card.meaning.trim()}
                          </p>
                        ) : null}
                        <p
                          style={{
                            margin: "4px 0 0",
                            fontSize: 11,
                            fontWeight: 600,
                            lineHeight: 1.4,
                            color: charcoal,
                          }}
                        >
                          {card.move}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </PdfCard>
          </div>
        ) : null}

        {teachingRows.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginTop: "auto",
              flexShrink: 0,
            }}
          >
            {teachingRows.slice(0, 2).map((row, index) => {
              const label =
                typeof row.star === "string" && row.star.trim().length > 0
                  ? row.star.trim()
                  : "How to work this stage";
              const cue = typeof row.cue === "string" ? row.cue.trim() : "";
              if (cue.length === 0) {
                return null;
              }
              return (
                <PdfInsightStrip
                  key={`teach-${String(index)}-${label}`}
                  label={label}
                  text={cue}
                  variant={index === 0 ? "softCoral" : "softGold"}
                />
              );
            })}
          </div>
        ) : starRows.length > 0 ? (
          <div style={{ marginTop: "auto", flexShrink: 0 }}>
            <PdfInsightStrip
              label="How to use these stars"
              text="Lead with the first cue above before mid-month. Quiet effort without a visible checkpoint rarely pays this timing."
              variant="softGold"
            />
          </div>
        ) : null}
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterStars;
